import React, { useState, useEffect } from 'react';
import { getData, saveData, db } from '../firebase';
import { doc, deleteDoc } from 'firebase/firestore';
import { getUserProfile } from '../firebase';
import { PartyForm } from './Parties';
import { exportInvoicePDF } from '../exportUtils';
import { invoiceSettlement } from '../ledgers';
import { getIndustry, deriveFare, DEFAULT_INDUSTRY, COMMON_CURRENCIES } from '../industryConfig';

const BRAND = '#0f6e56';

function computeNet({ fareAmount, discountAmount, taxRate, taxAmount }) {
  const fare = parseFloat(fareAmount) || 0;
  const disc = parseFloat(discountAmount) || 0;
  const taxable = Math.max(0, fare - disc);
  const tax = (taxAmount !== '' && taxAmount != null) ? (parseFloat(taxAmount) || 0)
            : taxable * ((parseFloat(taxRate) || 0) / 100);
  return { fare, disc, taxable, tax, net: taxable + tax };
}

const baseForm = {
  type: 'sales', customerName: '', supplierName: '',
  fareAmount: '', discountAmount: '', taxRate: '', taxAmount: '', currency: '', fxRate: '',
  description: '', date: new Date().toISOString().slice(0, 10), dueDate: '', status: 'unpaid',
};

export default function Invoices({ user }) {
  const [invoices, setInvoices] = useState([]);
  const [parties, setParties] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [business, setBusiness] = useState(null);
  const [tab, setTab] = useState('sales');
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [showPartyForm, setShowPartyForm] = useState(false);
  const [shareInv, setShareInv] = useState(null); // invoice pending a base/forex share choice
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(baseForm);
  const [partyForm, setPartyForm] = useState({ type: 'customer', name: '', contactPerson: '', email: '', phone: '', address: '', city: '', country: '', openingBalance: '' });

  const cfg = getIndustry(business && business.industry ? business.industry : DEFAULT_INDUSTRY);
  const baseCurrency = (business && business.currency) ? business.currency : 'USD';
  const invCurrency = form.currency || baseCurrency;
  const isForeign = cfg.multiCurrency && invCurrency !== baseCurrency;
  const fxRate = isForeign ? (parseFloat(form.fxRate) || 0) : 1;

  const load = async () => {
    setLoading(true);
    const [inv, p, rec, pay] = await Promise.all([getData(user.uid, 'invoices'), getData(user.uid, 'parties'), getData(user.uid, 'receipts'), getData(user.uid, 'payments')]);
    setInvoices(inv); setParties(p); setReceipts(rec); setPayments(pay);
    getUserProfile(user.uid).then(setBusiness);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openForm = () => { setForm({ ...baseForm, type: tab }); setShowForm(true); };

  const setField = (k, v) => {
    setForm(f => {
      const next = { ...f, [k]: v };
      // auto-fill fare from qty*rate or hours*rate when applicable
      const derived = deriveFare(cfg, next);
      if (derived != null) next.fareAmount = String(derived);
      return next;
    });
  };

  const addInvoice = async (e) => {
    e.preventDefault();
    const party = form.type === 'sales' ? form.customerName : form.supplierName;
    const { fare, disc, tax, net } = computeNet(form);
    if (!party || net <= 0) return;
    if (isForeign && fxRate <= 0) { alert('Please enter the exchange rate for ' + invCurrency); return; }
    const rate = isForeign ? fxRate : 1;
    const count = invoices.filter(i => i.type === form.type).length + 1;
    const prefix = form.type === 'sales' ? 'INV' : 'PUR';
    await saveData(user.uid, 'invoices', {
      ...form,
      industry: cfg.id,
      currency: invCurrency,
      fxRate: rate,
      // foreign-currency figures (what the party sees)
      fareForeign: fare, netForeign: net, taxForeign: tax, discountForeign: disc,
      // BASE-currency figures drive all ledgers & reports
      fareAmount: fare * rate, discountAmount: disc * rate,
      taxRate: parseFloat(form.taxRate) || 0, taxAmount: tax * rate,
      amount: net * rate,
      invoiceNo: `${prefix}-${String(count).padStart(4, '0')}`,
    });
    setShowForm(false);
    setForm({ ...baseForm, type: tab });
    load();
  };

  const saveParty = async (e) => {
    e.preventDefault();
    if (!partyForm.name.trim()) return;
    await saveData(user.uid, 'parties', {
      ...partyForm,
      openingBalance: parseFloat(partyForm.openingBalance) || 0,
      head: partyForm.type === 'customer' ? 'Sundry Debtors' : 'Sundry Creditors',
      group: partyForm.type === 'customer' ? 'ASSET' : 'LIABILITY',
    });
    if (partyForm.type === 'customer') setForm(f => ({ ...f, customerName: partyForm.name }));
    else setForm(f => ({ ...f, supplierName: partyForm.name }));
    setShowPartyForm(false);
    setParties(await getData(user.uid, 'parties'));
  };

  const deleteInv = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    await deleteDoc(doc(db, 'users', user.uid, 'invoices', id));
    load();
  };

  const doShare = (inv, mode) => {
    const party = parties.find(p => p.name === (inv.type === 'sales' ? inv.customerName : inv.supplierName));
    exportInvoicePDF(inv, business, party, mode);
    setShareInv(null);
  };
  const onShareClick = (inv) => {
    if (inv.currency && business && inv.currency !== (business.currency || 'USD')) setShareInv(inv); // foreign -> ask
    else doShare(inv, 'base');
  };
  const fmt = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const filtered = invoices.filter(i => {
    if (i.type !== tab) return false;
    if (filter === 'all') return true;
    return invoiceSettlement(i, receipts, payments).status === filter;
  });
  const total = filtered.reduce((s, i) => s + (i.amount || 0), 0);
  const unpaidTotal = filtered.reduce((s, i) => s + invoiceSettlement(i, receipts, payments).outstanding, 0);
  const relevantParties = parties.filter(p => p.type === (form.type === 'sales' ? 'customer' : 'supplier'));
  const preview = computeNet(form);

  // Build a short travel/service/item summary line from configured fields
  const summaryLine = (inv) => {
    const bits = [];
    cfg.fields.forEach(fld => {
      if (fld.key === 'description') return;
      const val = inv[fld.key];
      if (val) {
        if (fld.key === 'pnr') bits.push(`PNR ${val}`);
        else if (fld.key === 'refNo') bits.push(`TKT ${val}`);
        else if (fld.key === 'hours') bits.push(`${val} hrs`);
        else if (fld.key === 'rate') bits.push(`@ ${val}`);
        else if (fld.key === 'qty') bits.push(`Qty ${val}`);
        else if (fld.type === 'date') { /* skip dates in summary */ }
        else bits.push(val);
      }
    });
    return bits.join(' · ');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>Invoices</h2>
          <div style={{ fontSize: 12, color: cfg.accent, fontWeight: 600, marginTop: 2 }}>{cfg.icon} {cfg.name} edition</div>
        </div>
        <button onClick={openForm} style={btn}>+ New Invoice</button>
      </div>

      <div style={{ display: 'flex', gap: 0, marginBottom: 20, background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', overflow: 'hidden', width: 'fit-content' }}>
        {[{ id: 'sales', label: 'Sales Invoices', color: BRAND }, { id: 'purchase', label: 'Purchase Invoices', color: '#a32d2d' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '12px 24px', border: 'none', cursor: 'pointer', background: tab === t.id ? t.color : '#fff', color: tab === t.id ? '#fff' : '#666', fontWeight: tab === t.id ? 600 : 400, fontSize: 14 }}>{t.label}</button>
        ))}
      </div>

      <div style={{ background: tab === 'sales' ? '#e1f5ee' : '#fcebeb', borderRadius: 12, padding: '14px 20px', marginBottom: 16, display: 'flex', gap: 32 }}>
        <div><div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase' }}>Total</div><div style={{ fontSize: 20, fontWeight: 700, color: tab === 'sales' ? BRAND : '#a32d2d' }}>{fmt(total)}</div></div>
        <div><div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase' }}>Unpaid</div><div style={{ fontSize: 20, fontWeight: 700, color: '#854f0b' }}>{fmt(unpaidTotal)}</div></div>
        <div><div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase' }}>Count</div><div style={{ fontSize: 20, fontWeight: 700, color: '#555' }}>{filtered.length}</div></div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['all', 'unpaid', 'partial', 'paid'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ padding: '6px 16px', borderRadius: 20, border: `1px solid ${filter === f ? (tab === 'sales' ? BRAND : '#a32d2d') : '#ddd'}`, background: filter === f ? (tab === 'sales' ? BRAND : '#a32d2d') : '#fff', color: filter === f ? '#fff' : '#666', cursor: 'pointer', fontSize: 13, fontWeight: filter === f ? 600 : 400 }}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
        ))}
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>Loading...</div> : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#aaa', background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
          <div style={{ fontSize: 16, fontWeight: 500 }}>No {tab} invoices yet</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(inv => {
            const party = inv.type === 'sales' ? inv.customerName : inv.supplierName;
            const settle = invoiceSettlement(inv, receipts, payments);
            const isOverdue = inv.dueDate && new Date(inv.dueDate) < new Date() && settle.status !== 'paid';
            const statusStyle = {
              paid: { bg: '#e1f5ee', color: BRAND, label: '✓ Paid' },
              partial: { bg: '#e6f1fb', color: '#185fa5', label: 'Partial' },
              unpaid: { bg: '#faeeda', color: '#854f0b', label: 'Unpaid' },
            }[settle.status];
            const person = inv.paxName || inv.staffName || '';
            const sLine = summaryLine(inv);
            return (
              <div key={inv.id} style={{ background: '#fff', borderRadius: 12, padding: 20, border: `1px solid ${isOverdue ? '#f09595' : '#e8e8e8'}`, display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: '#888' }}>{inv.invoiceNo}</span>
                    {isOverdue && <span style={{ background: '#fcebeb', color: '#a32d2d', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>OVERDUE</span>}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{party}{person ? <span style={{ fontWeight: 400, color: '#666', fontSize: 14 }}> — {person}</span> : null}</div>
                  {sLine && <div style={{ fontSize: 12, color: cfg.accent, marginTop: 2 }}>{sLine}</div>}
                  <div style={{ fontSize: 13, color: '#888' }}>{inv.description}{inv.description ? ' · ' : ''}{inv.date}{inv.dueDate ? ` · Due: ${inv.dueDate}` : ''}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: inv.type === 'sales' ? '#222' : '#a32d2d', marginBottom: 2 }}>{inv.currency && inv.currency !== baseCurrency ? `${inv.currency} ${Number(inv.netForeign || 0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}` : fmt(inv.amount)}</div>
                  {inv.currency && inv.currency !== baseCurrency && <div style={{ fontSize: 10, color: '#999', marginBottom: 2 }}>{baseCurrency} {Number(inv.amount||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} @ {inv.fxRate}</div>}
                  {(inv.taxAmount > 0 || inv.discountAmount > 0) && (
                    <div style={{ fontSize: 10, color: '#999', marginBottom: 2 }}>
                      {inv.discountAmount > 0 ? `Disc ${fmt(inv.discountAmount)}` : ''}{inv.discountAmount > 0 && inv.taxAmount > 0 ? ' · ' : ''}{inv.taxAmount > 0 ? `Tax ${fmt(inv.taxAmount)}` : ''}
                    </div>
                  )}
                  {settle.status === 'partial' && <div style={{ fontSize: 11, color: '#854f0b', marginBottom: 4 }}>Outstanding {fmt(settle.outstanding)}</div>}
                  <span style={{ display: 'inline-block', padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: statusStyle.bg, color: statusStyle.color }}>{statusStyle.label}</span>
                </div>
                <button onClick={() => onShareClick(inv)} title="Share / Print invoice" style={{ background: '#e1f5ee', border: 'none', cursor: 'pointer', color: BRAND, fontSize: 13, fontWeight: 600, padding: '8px 12px', borderRadius: 8 }}>📤 Share</button>
                <button onClick={() => deleteInv(inv.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e24b4a', fontSize: 18 }}>🗑</button>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 560, maxHeight: '92vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>New {form.type === 'sales' ? 'Sales' : 'Purchase'} {cfg.invoiceNoun}</div>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
            </div>
            <form onSubmit={addInvoice}>
              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                {[{ v: 'sales', l: 'Sales', sub: 'To ' + cfg.customerLabel.toLowerCase() }, { v: 'purchase', l: 'Purchase', sub: 'From ' + cfg.supplierLabel.toLowerCase() }].map(t => (
                  <button key={t.v} type="button" onClick={() => setForm(f => ({ ...f, type: t.v }))} style={{ flex: 1, padding: 12, borderRadius: 10, border: `1.5px solid ${form.type === t.v ? (t.v === 'sales' ? BRAND : '#a32d2d') : '#ddd'}`, background: form.type === t.v ? (t.v === 'sales' ? BRAND : '#a32d2d') : '#fff', color: form.type === t.v ? '#fff' : '#555', cursor: 'pointer' }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{t.l}</div>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>{t.sub}</div>
                  </button>
                ))}
              </div>

              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>{form.type === 'sales' ? cfg.customerLabel : cfg.supplierLabel} *</label>
                  <button type="button" onClick={() => { setPartyForm(p => ({ ...p, type: form.type === 'sales' ? 'customer' : 'supplier' })); setShowPartyForm(true); }} style={{ background: 'none', border: 'none', color: BRAND, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>+ Create new</button>
                </div>
                {relevantParties.length > 0 ? (
                  <select value={form.type === 'sales' ? form.customerName : form.supplierName}
                    onChange={e => setForm(f => form.type === 'sales' ? { ...f, customerName: e.target.value } : { ...f, supplierName: e.target.value })} style={inp}>
                    <option value="">Select...</option>
                    {relevantParties.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                  </select>
                ) : (
                  <input value={form.type === 'sales' ? form.customerName : form.supplierName}
                    onChange={e => setForm(f => form.type === 'sales' ? { ...f, customerName: e.target.value } : { ...f, supplierName: e.target.value })}
                    placeholder={'Type name or click "+ Create new"'} style={inp} />
                )}
              </div>

              {/* Industry-specific detail fields */}
              <div style={{ background: cfg.accent + '0c', border: `1px solid ${cfg.accent}33`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: cfg.accent, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>{cfg.detailsTitle}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {cfg.fields.map(fld => (
                    <FF key={fld.key} label={fld.label} type={fld.type || 'text'} value={form[fld.key] || ''} onChange={v => setField(fld.key, v)} placeholder={fld.placeholder} />
                  ))}
                </div>
              </div>

              {cfg.multiCurrency && (
                <div style={{ background: '#f5f8fc', border: '1px solid #dce7f3', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#185fa5', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>💱 Currency</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ marginBottom: 4 }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 6 }}>Invoice Currency</label>
                      <select value={invCurrency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} style={inp}>
                        {[baseCurrency, ...COMMON_CURRENCIES.filter(c => c !== baseCurrency)].map(c => <option key={c} value={c}>{c}{c === baseCurrency ? ' (base)' : ''}</option>)}
                      </select>
                    </div>
                    {isForeign && (
                      <FF label={`Exchange Rate (1 ${invCurrency} = ? ${baseCurrency}) *`} type="number" value={form.fxRate} onChange={v => setForm(f => ({ ...f, fxRate: v }))} placeholder="e.g. 3.64" />
                    )}
                  </div>
                  {isForeign && fxRate > 0 && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Books will record this in <b>{baseCurrency}</b> at rate {fxRate}. Net {invCurrency} {preview.net.toFixed(2)} = {baseCurrency} {(preview.net * fxRate).toFixed(2)}.</div>
                  )}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FF label={cfg.baseAmountLabel + ' *'} type="number" value={form.fareAmount} onChange={v => setForm(f => ({ ...f, fareAmount: v }))} placeholder="0.00" />
                <FF label="Discount" type="number" value={form.discountAmount} onChange={v => setForm(f => ({ ...f, discountAmount: v }))} placeholder="0.00" />
                <FF label="Tax Rate (%)" type="number" value={form.taxRate} onChange={v => setForm(f => ({ ...f, taxRate: v, taxAmount: '' }))} placeholder="e.g. 5" />
                <FF label="Tax Amount (override)" type="number" value={form.taxAmount} onChange={v => setForm(f => ({ ...f, taxAmount: v }))} placeholder="auto" />
              </div>

              <div style={{ background: '#0a3d2e', borderRadius: 12, padding: '14px 18px', margin: '6px 0 16px', color: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, opacity: 0.85, marginBottom: 4 }}><span>{cfg.baseAmountLabel}</span><span>{invCurrency} {preview.fare.toFixed(2)}</span></div>
                {preview.disc > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, opacity: 0.85, marginBottom: 4 }}><span>Less: Discount</span><span>− {invCurrency} {preview.disc.toFixed(2)}</span></div>}
                {preview.tax > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, opacity: 0.85, marginBottom: 4 }}><span>Add: Tax</span><span>+ {invCurrency} {preview.tax.toFixed(2)}</span></div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700, borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 8, marginTop: 4 }}><span>Net Total</span><span>{invCurrency} {preview.net.toFixed(2)}</span></div>
                {isForeign && fxRate > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, opacity: 0.7, marginTop: 6 }}><span>In base currency</span><span>{baseCurrency} {(preview.net * fxRate).toFixed(2)}</span></div>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FF label="Invoice Date" type="date" value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} />
                <FF label="Due Date" type="date" value={form.dueDate} onChange={v => setForm(f => ({ ...f, dueDate: v }))} />
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button type="button" onClick={() => setShowForm(false)} style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ flex: 2, ...btn }}>Save Invoice</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {shareInv && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 20 }} onClick={() => setShareInv(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>Download invoice as…</div>
            <div style={{ fontSize: 13, color: '#888', marginBottom: 18 }}>This invoice is in {shareInv.currency}. Choose which currency to show on the PDF.</div>
            <button onClick={() => doShare(shareInv, 'foreign')} style={{ ...btn, width: '100%', marginBottom: 10 }}>Invoice currency ({shareInv.currency})</button>
            <button onClick={() => doShare(shareInv, 'base')} style={{ width: '100%', padding: 12, borderRadius: 10, border: `1.5px solid ${BRAND}`, background: '#fff', color: BRAND, fontWeight: 600, cursor: 'pointer' }}>Base currency ({business && business.currency ? business.currency : 'base'})</button>
          </div>
        </div>
      )}

      {showPartyForm && <PartyForm form={partyForm} setForm={setPartyForm} onSubmit={saveParty} onClose={() => setShowPartyForm(false)} accent={partyForm.type === 'customer' ? BRAND : '#a32d2d'} />}
    </div>
  );
}

function FF({ label, type = 'text', value, onChange, placeholder }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 6 }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={inp} />
    </div>
  );
}

const btn = { padding: '10px 20px', background: BRAND, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const inp = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 10, fontSize: 14, color: '#222', outline: 'none', background: '#fff' };
